import React from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress } from '@mui/material';
import { isChunkLoadError, isReloadPending, reloadOnce, reloadPage } from '@/utils/reload-once';

interface Props {
  children: React.ReactNode;
  /** Wechselt er (z. B. der Pfad), beginnt die Seite von vorn. */
  resetKey?: string;
}

interface State {
  fehler: unknown;
}

/**
 * Eine Seite, die nicht geladen werden kann, sagt es — statt leer zu bleiben.
 *
 * Ohne diese Grenze riss ein Fehler beim Nachladen einer Seite die ganze
 * Oberfläche mit: React baut dann nichts mehr auf, und die Seite
 * „Einstellungen“ war beim ersten Aufruf nach dem Update weiß, der Grund stand
 * nur in der Konsole (Testgrundlage 2.2, T00 und B-01).
 *
 * Fehlt ein Bündel, wird einmal neu geladen (`reloadOnce`) — das holt die
 * aktuelle Fassung. Geht das nicht oder ist es ein anderer Fehler, erscheint
 * ein Hinweis mit „Neu laden“.
 */
export class PageErrorBoundary extends React.Component<Props, State> {
  state: State = { fehler: null };

  static getDerivedStateFromError(fehler: unknown): State {
    return { fehler: fehler ?? new Error('Unbekannter Fehler') };
  }

  componentDidCatch(fehler: unknown): void {
    // Für die Fehlersuche bleibt der Grund in der Konsole.
    console.error('Seite konnte nicht angezeigt werden:', fehler);
    // Statt des Hinweises den Ladekreis, bis die neue Fassung da ist.
    if (isChunkLoadError(fehler) && reloadOnce()) this.forceUpdate();
  }

  componentDidUpdate(vorher: Props): void {
    if (this.state.fehler && vorher.resetKey !== this.props.resetKey) {
      this.setState({ fehler: null });
    }
  }

  render(): React.ReactNode {
    const { fehler } = this.state;
    if (!fehler) return this.props.children;

    if (isReloadPending()) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress aria-label="Die aktuelle Version wird geladen" />
        </Box>
      );
    }

    const buendelFehlt = isChunkLoadError(fehler);
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reloadPage}>
              Neu laden
            </Button>
          }
        >
          <AlertTitle>Diese Seite konnte nicht geladen werden.</AlertTitle>
          {buendelFehlt
            ? 'Wahrscheinlich wurde revilion inzwischen aktualisiert. Bitte laden Sie die Seite neu.'
            : 'Bitte laden Sie die Seite neu. Besteht der Fehler weiter, melden Sie bitte Uhrzeit und Schritt.'}
        </Alert>
      </Box>
    );
  }
}
